import {
  AxisOptions,
  AxisOptionsBase,
  Chart as ReactChart,
} from "react-charts";
import { Icon, Progress } from "trunx";
import { useMemo } from "react";

import useChartData, { ChartData, Datum } from "../hooks/useChartData";
import { observer } from "mobx-react-lite";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { decimalPlaces, useLocaleContext } from "../contexts/LocaleContext";
import {
  ISelectedSensor,
  SelectedPrecipSensor,
} from "../models/SelectedSensor";

const ChartWithData = ({
  selected,
  chartData,
}: {
  selected: ISelectedSensor;
  chartData: ChartData;
}) => {
  const { dict } = useLocaleContext();

  const precip = SelectedPrecipSensor.is(selected);
  const elementType: AxisOptionsBase["elementType"] = precip ? "bar" : "line";

  const primaryAxis = useMemo(
    (): AxisOptions<Datum> => ({
      getValue: (datum) => datum.ts,
      scaleType: "localTime",
      min: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
    }),
    []
  );

  const { data, unit } = chartData;
  const unitPlaces = decimalPlaces(unit);

  const secondaryAxes = useMemo(
    (): AxisOptions<Datum>[] => [
      {
        getValue: (datum) => datum.value,
        elementType,
        formatters: {
          scale: (value: number) => (value ? value.toFixed(unitPlaces) : ""),
        },
      },
    ],
    [elementType, unitPlaces]
  );


  if (data.length === 0) {
    return (
      <div style={{ padding: "2rem 0", textAlign: "center" }}>
        <span className="icon-text">
          <Icon>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </Icon>
          <span>{dict.pane.chart.noData}&nbsp;</span>
        </span>
      </div>
    );
  }

  return (
    <ReactChart
      options={{
        data: [{ label: unit, data }],
        primaryAxis,
        secondaryAxes,
      }}
    />
  );
};

const Chart = ({ selected }: { selected: ISelectedSensor }) => {
  const chartData = useChartData(selected);

  if (!chartData) {
    return <Progress bulma="m-auto" style={{ width: "50%" }} max={100} />;
  }

  return <ChartWithData selected={selected} chartData={chartData} />;
};

export default observer(Chart);
